import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';

import { Globe, MapPin, AlertTriangle, Wrench, Users, Shield, TrendingUp, Filter, RefreshCw, Eye, Building, Layers, Target, Zap, ChevronRight, Search, Settings, Palette, Maximize2, Radio, Wifi, Database, Cpu, Server, Network } from 'lucide-react';
import { toast } from 'sonner';
import MapWorld from './MapWorld/MapWorld';
import { useCartographieGlobale } from './useCartographieGlobale';
import CartographieModals from './CartographieModals';

export default function CartographieGlobale({ getAuthHeader }) {
  const {
    loading, geoSyncing, geoSyncResult, mapStats, selectedView, setSelectedView,
    timeRange, setTimeRange, territoireFilter, setTerritoireFilter, recentActivity,
    isFullscreen, setIsFullscreen, showLegendModal, setShowLegendModal,
    showFilterModal, setShowFilterModal, customLegends, customFilters,
    newLegend, setNewLegend, newFilter, setNewFilter, searchTerritory, setSearchTerritory,
    handleSyncGPS, addCustomLegend, removeLegend, toggleLegend,
    addCustomFilter, removeFilter, toggleFilter,
    filteredTerritoires, totalStats, openFullscreenMap, loadData,
  } = useCartographieGlobale(getAuthHeader);

  const getStatusColor = (status) => {
    switch (status) {
      case 'urgent': return 'bg-red-700';
      case 'success': return 'bg-green-700';
      case 'info': return 'bg-blue-700';
      case 'warning': return 'bg-orange-700';
      default: return 'bg-gray-700';
    }
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case 'dommage': return <AlertTriangle className="h-4 w-4 text-red-400" />;
      case 'reparation': return <Wrench className="h-4 w-4 text-purple-400" />;
      case 'expert': return <Users className="h-4 w-4 text-blue-400" />;
      case 'rcds': return <Shield className="h-4 w-4 text-green-400" />;
      default: return <MapPin className="h-4 w-4 text-gray-400" />;
    }
  };

  return (
    <div className="space-y-6">
      {/* En-tête High-Tech */}
      <div className="high-tech-card rounded-2xl p-6 text-white shadow-2xl">
        <div className="cyber-grid absolute inset-0 opacity-30 rounded-2xl"></div>
        <div className="relative z-10">
          <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center node-active">
                <Globe className="h-8 w-8 text-white" />
              </div>
              <div>
                <h2 className="text-2xl font-bold flex items-center gap-3">
                  Cartographie Globale
                  <Badge className="bg-cyan-600 text-white font-medium ml-2">
                    <Wifi className="h-3 w-3 mr-1" />
                    LIVE
                  </Badge>
                </h2>
                <p className="text-blue-200 mt-1 flex items-center gap-2">
                  <Database className="h-4 w-4" />
                  Visualisation temps réel des dommages, réparations, experts et RCDS
                </p>
              </div>
            </div>
            
            <div className="flex flex-wrap items-center gap-2">
              {/* Filtre Vue */}
              <Select value={selectedView} onValueChange={setSelectedView}>
                <SelectTrigger className="w-44 bg-slate-800/50 border-blue-500/30 text-white">
                  <Eye className="h-4 w-4 mr-2" />
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tout afficher</SelectItem>
                  <SelectItem value="dommages">Dommages uniquement</SelectItem>
                  <SelectItem value="reparations">Réparations en cours</SelectItem>
                  <SelectItem value="experts">Experts assignés</SelectItem>
                  <SelectItem value="rcds">RCDS annuels</SelectItem>
                </SelectContent>
              </Select>
              
              {/* Filtre Période */}
              <Select value={timeRange} onValueChange={setTimeRange}>
                <SelectTrigger className="w-36 bg-slate-800/50 border-blue-500/30 text-white">
                  <Filter className="h-4 w-4 mr-2" />
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Toute période</SelectItem>
                  <SelectItem value="today">Aujourd&apos;hui</SelectItem>
                  <SelectItem value="week">Cette semaine</SelectItem>
                  <SelectItem value="month">Ce mois</SelectItem>
                  <SelectItem value="year">Cette année</SelectItem>
                </SelectContent>
              </Select>
              
              {/* Rafraîchir */}
              <Button 
                variant="outline" 
                onClick={loadData} 
                disabled={loading}
                className="bg-slate-800/50 border-blue-500/30 text-white hover:bg-blue-600/30"
              >
                <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
                Sync
              </Button>

              {/* V36.37 — Synchroniser positions GPS (rétro-géocodage Nominatim) */}
              <Button
                variant="outline"
                onClick={handleSyncGPS}
                disabled={geoSyncing}
                className="bg-emerald-900/40 border-emerald-500/40 text-emerald-200 hover:bg-emerald-600/30 hover:text-white disabled:opacity-60"
                data-testid="map-sync-gps-btn"
                title="Rétro-géocoder via OpenStreetMap les utilisateurs sans coordonnées GPS (jusqu'à 200 par clic, ~2-3 min)"
              >
                <MapPin className={`h-4 w-4 mr-2 ${geoSyncing ? 'animate-pulse' : ''}`} />
                {geoSyncing ? 'Sync GPS…' : 'Synchroniser GPS'}
              </Button>
              {geoSyncResult && !geoSyncing && (
                <Badge
                  className="bg-emerald-600 text-white border-0 text-xs ml-1"
                  data-testid="map-sync-gps-result"
                  title={`Géocodés: ${geoSyncResult.geocoded} · Échecs: ${geoSyncResult.failed} · Traités: ${geoSyncResult.processed}`}
                >
                  +{geoSyncResult.geocoded}
                </Badge>
              )}
            </div>
          </div>

          {/* Barre de données en flux */}
          <div className="mt-4 h-1 rounded-full data-stream opacity-60"></div>
        </div>
      </div>

      {/* Statistiques High-Tech */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
        {[
          { icon: AlertTriangle, value: mapStats?.dommages_actifs || totalStats.dommages, label: 'Dommages actifs', color: 'from-red-600 to-red-800', iconColor: 'text-red-400' },
          { icon: Wrench, value: mapStats?.reparations_en_cours || totalStats.reparations, label: 'Réparations', color: 'from-purple-600 to-purple-800', iconColor: 'text-purple-400' },
          { icon: Users, value: mapStats?.experts_assignes || totalStats.experts, label: 'Experts assignés', color: 'from-blue-600 to-blue-800', iconColor: 'text-blue-400' },
          { icon: Shield, value: mapStats?.rcd_annuels || totalStats.rcds, label: 'RCDS annuels', color: 'from-green-600 to-green-800', iconColor: 'text-green-400' },
          { icon: Building, value: mapStats?.chantiers || 0, label: 'Chantiers actifs', color: 'from-orange-600 to-orange-800', iconColor: 'text-orange-400' },
          { icon: Target, value: filteredTerritoires.length, label: 'Territoires', color: 'from-cyan-600 to-cyan-800', iconColor: 'text-cyan-400' },
        ].map((stat, index) => (
          <Card 
            key={index} 
            className="high-tech-card cursor-pointer hover:scale-105 transition-transform"
            onClick={() => setSelectedView(stat.label.toLowerCase().split(' ')[0])}
          >
            <CardContent className="pt-4 relative">
              <div className="cyber-grid absolute inset-0 opacity-20 rounded-lg"></div>
              <div className="relative z-10 flex items-center gap-3">
                <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${stat.color} flex items-center justify-center node-active`}>
                  <stat.icon className={`h-6 w-6 ${stat.iconColor}`} />
                </div>
                <div>
                  <div className="text-2xl font-bold text-white">{stat.value}</div>
                  <div className="text-xs text-blue-300 font-medium">{stat.label}</div>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Contenu principal */}
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Carte principale */}
        <Card className="lg:col-span-3 high-tech-card overflow-hidden">
          <CardHeader className="pb-2 relative z-10">
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center gap-2 text-white">
                <Network className="h-5 w-5 text-cyan-400" />
                Réseau Territorial Connecté
              </CardTitle>
              <div className="flex items-center gap-2">
                <Badge className="bg-green-700 text-white font-medium">
                  <Radio className="h-3 w-3 mr-1 animate-pulse" />
                  Temps réel
                </Badge>
                <Button 
                  size="sm" 
                  variant="outline" 
                  onClick={openFullscreenMap}
                  className="bg-slate-800/50 border-blue-500/30 text-white hover:bg-blue-600/30"
                >
                  <Maximize2 className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent className="p-0 relative">
            <div className="cyber-grid absolute inset-0 opacity-10"></div>
            <div className="h-[500px] rounded-b-lg overflow-hidden relative z-10">
              <MapWorld isAdminView={true} getAuthHeader={getAuthHeader} />
            </div>
            {/* Overlay effet hologramme */}
            <div className="absolute inset-0 pointer-events-none hologram-effect rounded-b-lg"></div>
          </CardContent>
        </Card>
        
        {/* Panneau latéral */}
        <div className="space-y-4">
          {/* Activités récentes */}
          <Card className="high-tech-card">
            <CardHeader className="pb-2 relative z-10">
              <CardTitle className="text-lg flex items-center gap-2 text-white">
                <Zap className="h-5 w-5 text-yellow-400 animate-pulse" />
                Flux d&apos;activité
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3 relative z-10">
              {recentActivity.length === 0 ? (
                <div className="text-center py-6 text-blue-300/70 text-sm">
                  Aucune activité récente
                </div>
              ) : recentActivity.map((activity, index) => (
                <div 
                  key={index}
                  className="flex items-start gap-3 p-3 bg-slate-800/50 rounded-lg hover:bg-slate-700/50 transition-colors cursor-pointer border border-blue-500/20"
                >
                  <div className="mt-0.5 node-active">
                    {getTypeIcon(activity.type)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-white truncate">{activity.text}</p>
                    <p className="text-xs text-blue-300">{activity.location}</p>
                    <p className="text-xs text-slate-400 mt-1">{activity.time}</p>
                  </div>
                  <Badge className={`${getStatusColor(activity.status)} text-white text-xs font-medium`}>
                    {activity.status === 'urgent' ? 'Urgent' : 
                     activity.status === 'success' ? 'OK' : 
                     activity.status === 'info' ? 'Info' : 'Alerte'}
                  </Badge>
                </div>
              ))}
            </CardContent>
          </Card>
          
          {/* Légende personnalisable */}
          <Card className="high-tech-card">
            <CardHeader className="pb-2 relative z-10">
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg flex items-center gap-2 text-white">
                  <Layers className="h-5 w-5 text-purple-400" />
                  Légende
                </CardTitle>
                <Button 
                  size="sm" 
                  variant="ghost" 
                  onClick={() => setShowLegendModal(true)}
                  className="text-blue-400 hover:text-blue-300 hover:bg-blue-500/20"
                >
                  <Settings className="h-4 w-4" />
                </Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-2 relative z-10">
              {customLegends.filter(l => l.active).map((legend) => (
                <div 
                  key={legend.id} 
                  className="flex items-center gap-3 p-2 rounded-lg hover:bg-slate-700/50 cursor-pointer transition-colors"
                  onClick={() => toggleLegend(legend.id)}
                >
                  <div 
                    className="w-4 h-4 rounded-full node-active" 
                    style={{ backgroundColor: legend.color }}
                  ></div>
                  <span className="text-sm text-blue-100">{legend.label}</span>
                </div>
              ))}
            </CardContent>
          </Card>
          
          {/* Actions rapides */}
          <Card className="high-tech-card">
            <CardHeader className="pb-2 relative z-10">
              <CardTitle className="text-lg text-white flex items-center gap-2">
                <Cpu className="h-5 w-5 text-cyan-400" />
                Actions rapides
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2 relative z-10">
              <Button 
                variant="outline" 
                className="w-full justify-start bg-slate-800/50 border-blue-500/30 text-white hover:bg-blue-600/30"
                onClick={() => {
                  setSearchTerritory('');
                  toast.success('Recherche réinitialisée');
                }}
              >
                <Search className="h-4 w-4 mr-2 text-blue-400" />
                Rechercher sur la carte
              </Button>
              <Button 
                variant="outline" 
                className="w-full justify-start bg-slate-800/50 border-blue-500/30 text-white hover:bg-blue-600/30"
                onClick={openFullscreenMap}
              >
                <Maximize2 className="h-4 w-4 mr-2 text-purple-400" />
                Mode plein écran
              </Button>
              <Button 
                variant="outline" 
                className="w-full justify-start bg-slate-800/50 border-blue-500/30 text-white hover:bg-blue-600/30"
                onClick={() => setShowFilterModal(true)}
              >
                <Filter className="h-4 w-4 mr-2 text-green-400" />
                Gérer les filtres
              </Button>
              <Button 
                variant="outline" 
                className="w-full justify-start bg-slate-800/50 border-blue-500/30 text-white hover:bg-blue-600/30"
                onClick={() => setShowLegendModal(true)}
              >
                <Palette className="h-4 w-4 mr-2 text-orange-400" />
                Personnaliser légende
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Statistiques par Territoire - Style High-Tech */}
      <Card className="high-tech-card overflow-hidden">
        <CardHeader className="relative z-10">
          <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
            <CardTitle className="flex items-center gap-2 text-white">
              <Server className="h-5 w-5 text-cyan-400" />
              Analyse par Territoire
              <Badge className="bg-purple-700 text-white font-medium ml-2">
                {filteredTerritoires.length} zones
              </Badge>
            </CardTitle>
            <div className="flex items-center gap-3">
              {/* Recherche territoire */}
              <div className="relative">
                <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-blue-400" />
                <Input
                  placeholder="Rechercher un territoire..."
                  value={searchTerritory}
                  onChange={(e) => setSearchTerritory(e.target.value)}
                  className="pl-9 w-64 bg-slate-800/50 border-blue-500/30 text-white placeholder:text-slate-400"
                />
              </div>
              
              {/* Filtre par statistique */}
              <Select value={territoireFilter} onValueChange={setTerritoireFilter}>
                <SelectTrigger className="w-48 bg-slate-800/50 border-blue-500/30 text-white">
                  <TrendingUp className="h-4 w-4 mr-2" />
                  <SelectValue placeholder="Trier par..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tous les territoires</SelectItem>
                  <SelectItem value="dommages">Plus de dommages</SelectItem>
                  <SelectItem value="reparations">Plus de réparations</SelectItem>
                  <SelectItem value="experts">Plus d&apos;experts</SelectItem>
                  <SelectItem value="rcds">Plus de RCDS</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardHeader>
        <CardContent className="relative z-10">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-800/80 border-b border-blue-500/30">
                <tr>
                  <th className="px-4 py-3 text-left text-sm font-medium text-blue-300">Territoire</th>
                  <th className="px-4 py-3 text-center text-sm font-medium text-blue-300">Code</th>
                  <th className="px-4 py-3 text-center text-sm font-medium text-red-400">Dommages</th>
                  <th className="px-4 py-3 text-center text-sm font-medium text-purple-400">Réparations</th>
                  <th className="px-4 py-3 text-center text-sm font-medium text-blue-400">Experts</th>
                  <th className="px-4 py-3 text-center text-sm font-medium text-green-400">RCDS</th>
                  <th className="px-4 py-3 text-center text-sm font-medium text-orange-400">COOPER&apos;S</th>
                  <th className="px-4 py-3 text-right text-sm font-medium text-blue-300">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-blue-500/20">
                {filteredTerritoires.map((territoire, index) => (
                  <tr key={territoire.id || index} className="hover:bg-slate-700/50 transition-colors">
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <div className="w-2 h-2 rounded-full bg-cyan-400 node-active"></div>
                        <span className="font-medium text-white">{territoire.nom}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <Badge className="bg-slate-700 text-white text-cyan-300 font-mono font-semibold">
                        {territoire.code || 'N/A'}
                      </Badge>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <Badge className="bg-red-700 text-white font-medium">{territoire.dommages || 0}</Badge>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <Badge className="bg-purple-700 text-white font-medium">{territoire.reparations || 0}</Badge>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <Badge className="bg-blue-700 text-white font-medium">{territoire.experts || 0}</Badge>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <Badge className="bg-green-700 text-white font-medium">{territoire.rcds || 0}</Badge>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <Badge className="bg-orange-700 text-white font-medium">{territoire.coopers || 0}</Badge>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <Button 
                        size="sm" 
                        variant="ghost" 
                        className="text-cyan-400 hover:text-cyan-300 hover:bg-cyan-500/20"
                        onClick={() => {
                          setTerritoireFilter(territoire.id);
                          toast.success(`Focus sur ${territoire.nom}`);
                        }}
                      >
                        <ChevronRight className="h-4 w-4" />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
              {/* Totaux */}
              <tfoot className="bg-slate-800/80 border-t-2 border-cyan-500/50">
                <tr>
                  <td className="px-4 py-3 font-bold text-cyan-300">TOTAL</td>
                  <td className="px-4 py-3 text-center">
                    <Badge className="bg-cyan-700 text-white font-medium">{filteredTerritoires.length}</Badge>
                  </td>
                  <td className="px-4 py-3 text-center">
                    <Badge className="bg-red-800 text-white font-bold">{totalStats.dommages}</Badge>
                  </td>
                  <td className="px-4 py-3 text-center">
                    <Badge className="bg-purple-800 text-white font-bold">{totalStats.reparations}</Badge>
                  </td>
                  <td className="px-4 py-3 text-center">
                    <Badge className="bg-blue-800 text-white font-bold">{totalStats.experts}</Badge>
                  </td>
                  <td className="px-4 py-3 text-center">
                    <Badge className="bg-green-800 text-white font-bold">{totalStats.rcds}</Badge>
                  </td>
                  <td className="px-4 py-3 text-center">
                    <Badge className="bg-orange-800 text-white font-bold">{totalStats.coopers}</Badge>
                  </td>
                  <td className="px-4 py-3"></td>
                </tr>
              </tfoot>
            </table>
          </div>
        </CardContent>
      </Card>

      <CartographieModals
        showLegendModal={showLegendModal} setShowLegendModal={setShowLegendModal}
        newLegend={newLegend} setNewLegend={setNewLegend} addCustomLegend={addCustomLegend}
        customLegends={customLegends} toggleLegend={toggleLegend} removeLegend={removeLegend}
        showFilterModal={showFilterModal} setShowFilterModal={setShowFilterModal}
        newFilter={newFilter} setNewFilter={setNewFilter} addCustomFilter={addCustomFilter}
        customFilters={customFilters} toggleFilter={toggleFilter} removeFilter={removeFilter}
        isFullscreen={isFullscreen} setIsFullscreen={setIsFullscreen} getAuthHeader={getAuthHeader}
      />
    </div>
  );
}
